import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../hooks/useApi';
import DatePicker, { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import 'react-datepicker/dist/react-datepicker.css';
import Icon from '../components/Icon';
import './Evenements.css';

registerLocale('fr', fr);

// ─── Helpers ──────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' });
}

function fmt$(v) {
  if (v == null) return '0 $';
  return parseFloat(v).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });
}

// ─── Config ───────────────────────────────────

const PUBLIC_FORM_URL = process.env.REACT_APP_PUBLIC_FORM_URL || 'http://localhost:3002';

// ─── Badges statut ────────────────────────────

const STATUTS = {
  brouillon: { label: 'Brouillon', couleur: 'var(--muted)',   bg: 'color-mix(in srgb, var(--muted) 12%, transparent)',   border: 'color-mix(in srgb, var(--muted) 25%, transparent)' },
  publie:    { label: 'Publié',    couleur: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 12%, transparent)', border: 'color-mix(in srgb, var(--success) 30%, transparent)' },
  complet:   { label: 'Complet',   couleur: 'var(--accent)',  bg: 'color-mix(in srgb, var(--accent) 12%, transparent)',  border: 'color-mix(in srgb, var(--accent) 30%, transparent)' },
  termine:   { label: 'Terminé',   couleur: 'var(--info)',    bg: 'color-mix(in srgb, var(--info) 12%, transparent)',    border: 'color-mix(in srgb, var(--info) 30%, transparent)' },
  annule:    { label: 'Annulé',    couleur: 'var(--danger)',  bg: 'color-mix(in srgb, var(--danger) 10%, transparent)',  border: 'color-mix(in srgb, var(--danger) 25%, transparent)' },
};

const TRANSITIONS_LABELS = {
  publie:    'Publier',
  brouillon: 'Dépublier',
  complet:   'Marquer complet',
  termine:   'Terminer',
  annule:    'Annuler',
};

const TRANSITIONS_VALIDES = {
  brouillon: ['publie', 'annule'],
  publie:    ['brouillon', 'complet', 'annule'],
  complet:   ['publie', 'termine'],
  termine:   [],
  annule:    [],
};

function BadgeStatut({ event, onStatutChange }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState(null);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const options = TRANSITIONS_VALIDES[event.statut] || [];
  const config = STATUTS[event.statut] || { label: event.statut, couleur: 'var(--muted)', bg: 'color-mix(in srgb, var(--muted) 10%, transparent)', border: 'var(--border)' };

  const changerStatut = async (nouveau_statut, confirme = false) => {
    setLoading(true);
    try {
      const res = await api.put(`/evenements/${event.id}/statut`, { statut: nouveau_statut, confirme });
      if (res.warning) {
        setWarning({ message: res.message, statut_cible: nouveau_statut });
        setLoading(false);
        return;
      }
      onStatutChange(event.id, nouveau_statut);
      setOpen(false);
      setWarning(null);
    } catch (err) {
      alert(err.response?.data?.error || err?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="badge-statut-wrapper" ref={ref} onClick={e => e.stopPropagation()}>
      <button
        className="badge-statut"
        style={{ color: config.couleur, background: config.bg, border: `1px solid ${config.border}` }}
        onClick={() => options.length > 0 && setOpen(o => !o)}
        disabled={options.length === 0 || loading}
      >
        {config.label}
        {options.length > 0 && <span className="badge-chevron">▾</span>}
      </button>

      {open && options.length > 0 && (
        <div className="statut-dropdown">
          <div className="statut-dropdown-titre">Changer le statut</div>
          {options.map(opt => (
            <button
              key={opt}
              className={`statut-option${opt === 'annule' ? ' danger' : ''}`}
              onClick={() => { changerStatut(opt); setOpen(false); }}
            >
              {TRANSITIONS_LABELS[opt]}
            </button>
          ))}
        </div>
      )}

      {warning && (
        <div className="modal-overlay" onClick={() => setWarning(null)}>
          <div className="modal-warning" onClick={e => e.stopPropagation()}>
            <h3>Attention</h3>
            <p>{warning.message}</p>
            <div className="modal-warning-actions">
              <button className="btn btn-ghost" onClick={() => setWarning(null)}>Annuler</button>
              <button
                className="btn btn-danger"
                onClick={() => { setWarning(null); changerStatut(warning.statut_cible, true); }}
              >
                Confirmer quand même
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helper slug ──────────────────────────────

function slugifier(nom, dateStr) {
  const moisFR = ['jan','fev','mar','avr','mai','jun','jul','aou','sep','oct','nov','dec'];
  const d = dateStr ? new Date(dateStr) : null;
  const mois = d ? moisFR[d.getMonth()] : null;
  const annee = d ? d.getFullYear() : null;
  const mots_a_retirer = ['escamel','bbq','artisans','du','fumoir','le','la','les','de','des','et','au','aux','un','une'];
  const slug_base = nom
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .split(/\s+/)
    .filter(mot => !mots_a_retirer.includes(mot) && mot.length > 1)
    .slice(0, 2)
    .join('-');
  if (!slug_base && !mois) return '';
  return slug_base && mois ? `${slug_base}-${mois}-${annee}` : mois ? `event-${mois}-${annee}` : slug_base;
}

function sanitiserSlug(val) {
  return val.toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

const VIDE = {
  nom: '',
  slug: '',
  slug_manuel: false,
  date_evenement: '',
  lieu: '',
  adresse: '',
  ville: '',
  description: '',
  capacite_totale: 200,
  capacite_slot: 40,
  date_limite_precommande: '',
  type: 'public',
  type_paiement: 'unique',
  client_corpo_entreprise: '',
  client_corpo_nom: '',
  client_corpo_email: '',
  client_corpo_tel: '',
  montant_corpo: '',
  acompte_pct: 50,
  notes_client: '',
};

// ─── Modal Nouvel événement ───────────────────

function ModalNouvelEvenement({ onClose, onCreated }) {
  const [form, setForm] = useState(VIDE);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);
  const [slugStatut, setSlugStatut] = useState(null);
  const slugDebounce = useRef(null);

  function setChamp(k, v) {
    setForm(f => {
      const next = { ...f, [k]: v };
      if ((k === 'nom' || k === 'date_evenement') && !f.slug_manuel) {
        next.slug = slugifier(k === 'nom' ? v : f.nom, k === 'date_evenement' ? v : f.date_evenement);
      }
      return next;
    });
  }

  function setSlug(val) {
    const propre = sanitiserSlug(val);
    setForm(f => ({ ...f, slug: propre, slug_manuel: true }));
  }

  useEffect(() => {
    if (!form.slug) { setSlugStatut(null); return; }
    setSlugStatut('verification');
    clearTimeout(slugDebounce.current);
    slugDebounce.current = setTimeout(async () => {
      try {
        const res = await api.get(`/evenements/check-slug?slug=${encodeURIComponent(form.slug)}`);
        setSlugStatut(res.disponible ? 'disponible' : 'indisponible');
      } catch { setSlugStatut(null); }
    }, 500);
    return () => clearTimeout(slugDebounce.current);
  }, [form.slug]);

  const montantAcompte = form.montant_corpo && form.acompte_pct
    ? (parseFloat(form.montant_corpo) * form.acompte_pct / 100).toFixed(2)
    : null;
  const montantSolde = form.montant_corpo && form.acompte_pct
    ? (parseFloat(form.montant_corpo) * (1 - form.acompte_pct / 100)).toFixed(2)
    : null;

  async function soumettre(e) {
    e.preventDefault();
    if (!form.nom.trim() || !form.date_evenement || !form.lieu.trim()) {
      setErreur('Nom, date et lieu sont obligatoires.');
      return;
    }
    if (form.type === 'corporatif' && (!form.client_corpo_email.trim() || !form.montant_corpo)) {
      setErreur('Email contact et montant total sont obligatoires pour un événement corporatif.');
      return;
    }
    setChargement(true);
    setErreur('');
    try {
      const payload = {
        nom:          form.nom,
        slug:         form.slug || undefined,
        date_event:   form.date_evenement,
        lieu:         form.lieu,
        adresse:      form.adresse,
        ville:        form.ville,
        description:  form.description,
        capacite_totale:   form.capacite_totale,
        capacite_par_slot: form.capacite_slot,
        date_limite_precommande: form.date_limite_precommande || undefined,
        type:         form.type,
        type_paiement: form.type === 'corporatif' ? form.type_paiement : 'individuel',
        client_corpo_entreprise: form.client_corpo_entreprise || undefined,
        client_corpo_nom:        form.client_corpo_nom        || undefined,
        client_corpo_email:      form.client_corpo_email      || undefined,
        client_corpo_tel:        form.client_corpo_tel        || undefined,
        montant_corpo:  form.montant_corpo ? parseFloat(form.montant_corpo) : undefined,
        acompte_pct:    form.type === 'corporatif' ? parseFloat(form.acompte_pct) : 0,
        notes_client:   form.notes_client || undefined,
      };
      const data = await api.post('/evenements', payload);
      onCreated(data);
    } catch (err) {
      setErreur(err?.message || 'Erreur lors de la création.');
    } finally {
      setChargement(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box evenements-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-titre">Nouvel événement</h2>
          <button className="modal-close" onClick={onClose}>
            <Icon name="fermer" size={16} />
          </button>
        </div>

        <form onSubmit={soumettre} className="evenement-form">
          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Nom de l'événement *</label>
              <input
                type="text"
                value={form.nom}
                onChange={e => setChamp('nom', e.target.value)}
                placeholder="Ex : Marché de Noël — Stoneham 2026"
                autoFocus
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-full">
              <label>URL de l'événement</label>
              <div className="slug-field-wrapper">
                <span className="slug-prefix">escamel.ca/precommande/</span>
                <input
                  className="slug-input"
                  type="text"
                  value={form.slug}
                  onChange={e => setSlug(e.target.value)}
                  placeholder="genere-automatiquement"
                />
              </div>
              {form.slug && slugStatut && (
                <span className={`slug-badge ${slugStatut}`}>
                  {slugStatut === 'disponible'   && 'Disponible'}
                  {slugStatut === 'indisponible' && 'Deja utilise'}
                  {slugStatut === 'verification' && 'Verification…'}
                </span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date de l'événement *</label>
              <DatePicker
                selected={form.date_evenement ? new Date(form.date_evenement) : null}
                onChange={d => setChamp('date_evenement', d ? d.toISOString().split('T')[0] : '')}
                dateFormat="dd MMMM yyyy"
                locale="fr"
                placeholderText="Choisir une date"
                className="datepicker-input"
                calendarClassName="datepicker-calendar"
              />
            </div>
            <div className="form-group">
              <label>Date limite précommande</label>
              <DatePicker
                selected={form.date_limite_precommande ? new Date(form.date_limite_precommande) : null}
                onChange={d => setChamp('date_limite_precommande', d ? d.toISOString().split('T')[0] : '')}
                dateFormat="dd MMMM yyyy"
                locale="fr"
                placeholderText="Choisir une date"
                className="datepicker-input"
                calendarClassName="datepicker-calendar"
                isClearable
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Lieu *</label>
              <input
                type="text"
                value={form.lieu}
                onChange={e => setChamp('lieu', e.target.value)}
                placeholder="Ex : Arèna de Stoneham"
              />
            </div>
            <div className="form-group">
              <label>Ville</label>
              <input
                type="text"
                value={form.ville}
                onChange={e => setChamp('ville', e.target.value)}
                placeholder="Ex : Stoneham-et-Tewkesbury"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Adresse</label>
              <input
                type="text"
                value={form.adresse}
                onChange={e => setChamp('adresse', e.target.value)}
                placeholder="Ex : 456 ch. des Sommets"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Capacité totale</label>
              <input
                type="number" min={1}
                value={form.capacite_totale}
                onChange={e => setChamp('capacite_totale', parseInt(e.target.value) || 200)}
              />
            </div>
            <div className="form-group">
              <label>Capacité par slot</label>
              <input
                type="number" min={1}
                value={form.capacite_slot}
                onChange={e => setChamp('capacite_slot', parseInt(e.target.value) || 40)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={e => setChamp('description', e.target.value)}
                rows={3}
                placeholder="Notes ou description publique…"
              />
            </div>
          </div>

          {/* Type d'événement */}
          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Type d'événement</label>
              <div className="radio-group">
                <label className="radio-option">
                  <input type="radio" name="type" value="public"
                    checked={form.type === 'public'}
                    onChange={() => setChamp('type', 'public')}
                  />
                  <span>Public — Formulaire ouvert à tous</span>
                </label>
                <label className="radio-option">
                  <input type="radio" name="type" value="corporatif"
                    checked={form.type === 'corporatif'}
                    onChange={() => setChamp('type', 'corporatif')}
                  />
                  <span>Corporatif — Client unique avec page dédiée</span>
                </label>
              </div>
            </div>
          </div>

          {/* Champs corporatifs */}
          {form.type === 'corporatif' && (
            <>
              <div className="form-separateur"><span>Informations corporatives</span></div>

              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Entreprise cliente</label>
                  <input
                    type="text"
                    value={form.client_corpo_entreprise}
                    onChange={e => setChamp('client_corpo_entreprise', e.target.value)}
                    placeholder="Ex : XYZ Corp"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact (nom)</label>
                  <input
                    type="text"
                    value={form.client_corpo_nom}
                    onChange={e => setChamp('client_corpo_nom', e.target.value)}
                    placeholder="Ex : Jean Tremblay"
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input
                    type="tel"
                    value={form.client_corpo_tel}
                    onChange={e => setChamp('client_corpo_tel', e.target.value)}
                    placeholder="418-555-0000"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Email contact *</label>
                  <input
                    type="email"
                    value={form.client_corpo_email}
                    onChange={e => setChamp('client_corpo_email', e.target.value)}
                    placeholder="jean@xyz.com"
                  />
                </div>
              </div>

              <div className="form-separateur"><span>Mode de paiement</span></div>

              <div className="form-row">
                <div className="form-group form-group-full">
                  <div className="radio-group">
                    <label className="radio-option">
                      <input type="radio" name="type_paiement" value="unique"
                        checked={form.type_paiement === 'unique'}
                        onChange={() => setChamp('type_paiement', 'unique')}
                      />
                      <span>Paiement unique (l'entreprise paie tout)</span>
                    </label>
                    <label className="radio-option">
                      <input type="radio" name="type_paiement" value="individuel"
                        checked={form.type_paiement === 'individuel'}
                        onChange={() => setChamp('type_paiement', 'individuel')}
                      />
                      <span>Paiement individuel (chaque employé paie)</span>
                    </label>
                    <label className="radio-option">
                      <input type="radio" name="type_paiement" value="hybride"
                        checked={form.type_paiement === 'hybride'}
                        onChange={() => setChamp('type_paiement', 'hybride')}
                      />
                      <span>Hybride (acompte entreprise + balance individuel)</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Montant total négocié *</label>
                  <div className="input-suffix">
                    <input
                      type="number" min={0} step="0.01"
                      value={form.montant_corpo}
                      onChange={e => setChamp('montant_corpo', e.target.value)}
                      placeholder="1500.00"
                    />
                    <span>$</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Acompte demandé</label>
                  <div className="input-suffix">
                    <input
                      type="number" min={0} max={100}
                      value={form.acompte_pct}
                      onChange={e => setChamp('acompte_pct', parseInt(e.target.value) || 0)}
                    />
                    <span>%</span>
                  </div>
                  {montantAcompte && (
                    <div className="form-hint">
                      Acompte : {parseFloat(montantAcompte).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}
                      {' '}| Solde : {parseFloat(montantSolde).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Notes pour le client</label>
                  <textarea
                    value={form.notes_client}
                    onChange={e => setChamp('notes_client', e.target.value)}
                    rows={3}
                    placeholder="Visibles sur la page publique corporative…"
                  />
                </div>
              </div>
            </>
          )}

          {erreur && <div className="form-erreur">{erreur}</div>}

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={chargement}>
              {chargement ? 'Création…' : "Créer l'événement"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Skeleton card ────────────────────────────

function SkeletonGrille() {
  return (
    <div className="evenements-grille">
      {[0,1,2,3,4,5].map(i => (
        <div key={i} className="skeleton skel-ev-card" style={{ animationDelay: `${i * 60}ms` }} />
      ))}
    </div>
  );
}

// ─── Card événement ───────────────────────────

function EvenementCard({ ev, onClick, onStatutChange }) {
  const pctCapacite = ev.capacite_totale > 0
    ? Math.min(100, Math.round((ev.nb_personnes || 0) / ev.capacite_totale * 100))
    : 0;

  const fillColor = pctCapacite >= 90
    ? 'var(--danger)'
    : pctCapacite >= 70
    ? 'var(--warning)'
    : 'var(--accent)';

  return (
    <div className="ev-card card" onClick={onClick}>
      <div className="ev-card-header">
        <div className="ev-card-meta">
          <span className="ev-date muted">{fmtDate(ev.date_evenement)}</span>
          <BadgeStatut event={ev} onStatutChange={onStatutChange} />
        </div>
        <h3 className="ev-card-nom">{ev.nom}</h3>
        <div className="ev-card-lieu muted">
          <Icon name="transport" size={13} />
          {ev.lieu}{ev.ville ? ` — ${ev.ville}` : ''}
        </div>
      </div>

      <div className="ev-card-stats">
        <div className="ev-stat">
          <span className="ev-stat-val">{ev.nb_commandes || 0}</span>
          <span className="ev-stat-lbl muted">commandes</span>
        </div>
        <div className="ev-stat">
          <span className="ev-stat-val">{ev.nb_personnes || 0}</span>
          <span className="ev-stat-lbl muted">personnes</span>
        </div>
        <div className="ev-stat">
          <span className="ev-stat-val gold">{fmt$(ev.revenus_total)}</span>
          <span className="ev-stat-lbl muted">revenus</span>
        </div>
      </div>

      <div className="ev-capacite">
        <div className="ev-capacite-label">
          <span>{ev.nb_personnes || 0} / {ev.capacite_totale} personnes</span>
          <span>{pctCapacite}%</span>
        </div>
        <div className="ev-capacite-barre">
          <div className="ev-capacite-fill" style={{ width: `${pctCapacite}%`, background: fillColor }} />
        </div>
      </div>

      {ev.statut === 'publie' && ev.precommande_active === 1 && (
        <div className="ev-live-row" onClick={e => e.stopPropagation()}>
          <span className="ev-live-badge">
            <span className="ev-live-dot" />
            LIVE
          </span>
          <a
            className="ev-lien-public"
            href={`${PUBLIC_FORM_URL}/evenement/${ev.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ borderTop: 'none', margin: 0, padding: 0 }}
          >
            Voir la page live
          </a>
          <button
            className="btn btn-ghost ev-btn-copier"
            onClick={() => {
              const url = `${PUBLIC_FORM_URL}/evenement/${ev.slug}`;
              if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
            }}
            title="Copier le lien"
          >
            Copier
          </button>
        </div>
      )}
      {(ev.statut === 'publie' || ev.statut === 'complet') && ev.slug && !(ev.statut === 'publie' && ev.precommande_active === 1) && (
        <a
          className="ev-lien-public"
          href={`${PUBLIC_FORM_URL}/precommande/${ev.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
        >
          Voir le formulaire public
        </a>
      )}
    </div>
  );
}

// ─── Page principale ──────────────────────────

export default function Evenements() {
  const navigate = useNavigate();
  const [evenements, setEvenements] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [filtreStatut, setFiltreStatut] = useState('tous');

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const data = await api.get('/evenements');
      setEvenements(Array.isArray(data) ? data : []);
    } catch {
      setErreur('Impossible de charger les événements.');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  function handleCreated(ev) {
    setShowModal(false);
    navigate(`/evenements/${ev.id}`);
  }

  const evFiltres = filtreStatut === 'tous'
    ? evenements
    : evenements.filter(e => e.statut === filtreStatut);

  return (
    <div className="evenements-page">
      {/* Header */}
      <div className="evenements-header">
        <div className="evenements-header-text">
          <h1 className="evenements-titre">Événements</h1>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Gérez vos événements de vente et précommandes
          </p>
        </div>
        <div className="evenements-header-actions">
          <a
            href={PUBLIC_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost"
          >
            <Icon name="voir" size={15} />
            events.escamel.ca
          </a>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Icon name="ajouter" size={15} />
            Nouvel événement
          </button>
        </div>
      </div>

      {/* Filtres statut */}
      <div className="evenements-filtres">
        {[
          { key: 'tous',      label: 'Tous' },
          { key: 'brouillon', label: 'Brouillon' },
          { key: 'publie',    label: 'Publiés' },
          { key: 'complet',   label: 'Complets' },
          { key: 'termine',   label: 'Terminés' },
          { key: 'annule',    label: 'Annulés' },
        ].map(f => (
          <button
            key={f.key}
            className={`filtre-btn${filtreStatut === f.key ? ' filtre-btn-actif' : ''}`}
            onClick={() => setFiltreStatut(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {chargement ? (
        <SkeletonGrille />
      ) : erreur ? (
        <div className="error-inline">
          <Icon name="alerte" size={16} />
          {erreur}
        </div>
      ) : evFiltres.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Icon name="date" size={40} /></div>
          <div className="empty-state-title">Aucun événement</div>
          <div className="empty-state-sub muted">
            {filtreStatut !== 'tous'
              ? 'Aucun événement dans ce statut.'
              : 'Créez votre premier événement pour commencer.'}
          </div>
          {filtreStatut === 'tous' && (
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <Icon name="ajouter" size={15} />
              Créer le premier événement
            </button>
          )}
        </div>
      ) : (
        <div className="evenements-grille">
          {evFiltres.map(ev => (
            <EvenementCard
              key={ev.id}
              ev={ev}
              onClick={() => navigate(`/evenements/${ev.id}`)}
              onStatutChange={(id, nouveau_statut) => {
                setEvenements(evs => evs.map(e =>
                  e.id === id ? { ...e, statut: nouveau_statut } : e
                ));
              }}
            />
          ))}
        </div>
      )}

      {showModal && (
        <ModalNouvelEvenement
          onClose={() => setShowModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
